import * as z from 'zod';

import { zodDateOrString } from '../../utils';
import { MiCompanyCode } from './miCompanyCodes';

// Record code: 2 = NOD (first Notice of Delinquency), 3 = MDR (Monthly Delinquency Report).
export const MiAdrRecordCode = z.enum(['2', '3']);
export type MiAdrRecordCode = z.infer<typeof MiAdrRecordCode>;

// 00 = delinquent/not removed, 01 = reinstated, 02 = assumed, 03 = servicing sold,
// 04 = paid in full, 05 = claim paid (unreachable in v1, no claims model).
export const MiAdrRemovalCode = z.enum(['00', '01', '02', '03', '04', '05']);
export type MiAdrRemovalCode = z.infer<typeof MiAdrRemovalCode>;

// Reason-for-default codes (ADR code table 2). See QrpcReasonToMiAdrNonPayCode for the mapping.
export const MiAdrNonPayCode = z.enum([
  '01', // Death of borrower
  '02', // Illness
  '03', // Marital problems
  '04', // Unemployment
  '05', // Business failure
  '06', // Temporary curtailment of income
  '07', // Bankruptcy
  '08', // Over-obligated / excessive use of credit
  '09', // Casualty loss
  '10', // Moved / vacated
  '11', // Dissatisfied with property
  '12', // National emergency declaration
  '13', // Servicing problems
  '14', // Auto repairs
  '15', // See comments
]);
export type MiAdrNonPayCode = z.infer<typeof MiAdrNonPayCode>;

export const MiAdrOccupancyCode = z.enum(['1', '2', '3']); // 1 owner, 2 tenant, 3 vacant
export type MiAdrOccupancyCode = z.infer<typeof MiAdrOccupancyCode>;

export const MiAdrBankruptcyChapter = z.enum(['07', '11', '13']);
export type MiAdrBankruptcyChapter = z.infer<typeof MiAdrBankruptcyChapter>;

export const MiAdrMortgageCode = z.enum(['1', '2']); // 1 first lien, 2 second lien
export type MiAdrMortgageCode = z.infer<typeof MiAdrMortgageCode>;

// 1 = primary coverage only, 2 = primary + pool (both with the receiving insurer). V1 emits only 1 and 2.
export const MiAdrInsuranceIndicator = z.enum(['1', '2']);
export type MiAdrInsuranceIndicator = z.infer<typeof MiAdrInsuranceIndicator>;

export const MiAdrV1 = z.object({
  version: z.literal('1').describe('version'),

  // Identity / routing
  miCompanyCode: MiCompanyCode,
  certificateNumber: z.string().describe('string'),
  recordCode: MiAdrRecordCode,
  removalCode: MiAdrRemovalCode,

  // Borrower & property identity
  lenderLoanNumber: z.string().describe('string'),
  borrowerName: z.string().describe('string'),
  coBorrowerName: z.string().optional().describe('string'),
  borrowerMailingStreet: z.string().optional().describe('string'),
  borrowerMailingLine3: z.string().optional().describe('string'),
  borrowerMailingLine4: z.string().optional().describe('string'),
  borrowerMailingZip: z.string().optional().describe('string'),
  propertyStreet: z.string().optional().describe('string'),
  propertyCity: z.string().optional().describe('string'),
  propertyState: z.string().optional().describe('string'),
  propertyZip: z.string().optional().describe('string'),
  borrowerPhone: z.string().optional().describe('string'),
  borrowerPhoneWork: z.string().optional().describe('string'),
  borrowerSsn: z.string().optional().describe('string'),
  coBorrowerSsn: z.string().optional().describe('string'),

  // Amounts (dollar amounts; the fixed-width formatter owns implied-decimal rendering)
  currentPrincipalBalance: z.number().describe('currency'),
  delinquentPaymentAmount: z.number().describe('currency'),
  otherCharges: z.number().describe('currency'),
  totalMonthlyPayment: z.number().describe('currency'),
  principalAndInterestPayment: z.number().describe('currency'),
  payoffAmount: z.number().describe('currency'),
  interestBearingPrincipalBalance: z.number().describe('currency'),

  // Delinquency metrics
  monthsDelinquent: z.number().int().describe('number'),
  firstUnpaidInstallmentNumber: z.number().int().describe('number'),
  firstPaymentDefault: z.boolean().describe('boolean'),
  dueDateOfFirstUnpaidInstallment: zodDateOrString.describe('date'),
  lastInstallmentReceivedDate: zodDateOrString.optional().describe('date'),
  paymentHistory12Mo: z.string().describe('string'),
  nonPayCode: MiAdrNonPayCode.optional(),

  // Classification
  occupancyCode: MiAdrOccupancyCode.optional(),
  mortgageCode: MiAdrMortgageCode,
  miInsuranceIndicator: MiAdrInsuranceIndicator,
  investorName: z.string().optional().describe('string'),

  // Legal milestones
  foreclosureBeganDate: zodDateOrString.optional().describe('date'),
  foreclosureSaleScheduledDate: zodDateOrString.optional().describe('date'),
  foreclosureSaleHeldDate: zodDateOrString.optional().describe('date'),
  redemptionPeriodEndsDate: zodDateOrString.optional().describe('date'),
  voluntaryConveyanceAcceptedDate: zodDateOrString.optional().describe('date'),
  bankruptcyFiledDate: zodDateOrString.optional().describe('date'),
  bankruptcyChapter: MiAdrBankruptcyChapter.optional(),
  bankruptcyReliefDate: zodDateOrString.optional().describe('date'),
});
export type MiAdrV1 = z.infer<typeof MiAdrV1>;

export const MiAdr = MiAdrV1;
export type MiAdr = z.infer<typeof MiAdr>;
